/**
 * Portal entry guard (Phase G, impl v10).
 *
 * Layers the subscriber-state + onboarding-complete checks on top of the
 * Phase C `requireSubscriberOrOperator` auth guard. The portal renders only
 * for a subscriber whose `subscribers.status = 'active'` who has completed
 * onboarding (`onboarding_state.onboarding_status = 'eric_reviewed'`).
 *
 * Outcomes:
 *   - No session                  -> requireSubscriberOrOperator redirects to
 *                                     /auth/sign-in (Phase C contract).
 *   - Operator session            -> allowed through (operators can view the
 *                                     portal; the operator dashboard is /eric).
 *   - Subscriber, no subscriber   -> /auth/sign-out, which clears the session
 *     row yet                       and sends the user to the marketing site
 *                                     (https://yesplease.studio) where the
 *                                     discovery / subscribe flow lives after
 *                                     the 2026-05-28 architecture pivot.
 *                                     Provisioning either failed or the user
 *                                     is using a stale magic-link session that
 *                                     does not match any subscriber row.
 *   - Subscriber, status=pending  -> /os when the caller owns a live
 *     and owns an OS engagement      os_engagements row (Route A OS owner,
 *                                     proven by RLS via fetchOwnOsEngagement).
 *                                     The OS owner's home is the separate /os
 *                                     surface; without this they would be
 *                                     ejected to the marketing site and /os
 *                                     would be unreachable via normal sign-in.
 *   - Subscriber, status=pending  -> /auth/sign-out. Explicit assertion
 *     (any other non-served state)   (STEP-9 / BATCH-6 note): the portal serves
 *                                    only active|paused|canceled. A status that
 *                                    reverted active->pending (and is not an OS
 *                                    owner) must not slip through on the
 *                                    onboarding-complete check alone.
 *   - Subscriber, status=paused   -> /portal still loads but read-only-ish;
 *     or canceled                   we do NOT hard-block (pause preserves the
 *                                     context doc / playbook / backlog per
 *                                     DES-07, and the subscriber must be able
 *                                     to read them). We surface the state in
 *                                     the layout banner instead.
 *   - Subscriber, onboarding not  -> /onboarding (the bare token-resolution
 *     eric_reviewed                  route, routes/onboarding/index.tsx). It
 *                                     reads the token from the current user's
 *                                     onboarding_state row server-side and
 *                                     forwards to /onboarding/<token>. This
 *                                     replaces the Phase 1 stopgap that signed
 *                                     the user out and forced a marketing
 *                                     round trip + magic-link re-entry.
 *
 * The redirect targets are intentionally conservative: this guard never
 * 404s the portal (it is the subscriber's home), it only routes them to the
 * right place when they are not yet ready to use it.
 *
 * Coordination with Phase F: this guard READS `onboarding_state` only. It
 * does not import or modify any Phase F file. If Phase F changes the
 * onboarding-complete contract (the `eric_reviewed` terminal status), update
 * the constant below; do not edit Phase F.
 */

import { redirect } from '@tanstack/react-router';
import { getCurrentSession, getRoleFromSession } from './auth.js';
import { requireSubscriberOrOperator } from './route-guards.js';
import { fetchOwnOnboardingState, fetchOwnSubscriber } from './portal.js';
import { fetchOwnOsEngagement } from './os.js';

/**
 * The onboarding_state.onboarding_status value that means Eric has reviewed
 * the subscriber's context and the portal is ready to render. Mirrors the
 * `onboardingStatusSchema` terminal value in @studio/api-schemas.
 */
const ONBOARDING_COMPLETE_STATUS = 'eric_reviewed' as const;

export interface PortalEntryContext {
  isOperator: boolean;
  subscriberId: string | null;
  tier: 'studio' | 'pro' | null;
  subscriberStatus: 'active' | 'paused' | 'canceled' | null;
}

/**
 * Run from `/portal`'s `beforeLoad`. Throws `redirect(...)` to halt
 * navigation when the subscriber is not ready; returns a small context
 * object the layout can consume otherwise.
 */
export async function requirePortalEntry(opts?: {
  redirectFrom?: string;
}): Promise<PortalEntryContext> {
  // Phase C auth gate first (redirects unauthenticated users to sign-in).
  await requireSubscriberOrOperator(
    opts?.redirectFrom !== undefined ? { redirectFrom: opts.redirectFrom } : {},
  );

  const session = await getCurrentSession();
  // requireSubscriberOrOperator already guarantees a session; the null check
  // keeps the types honest.
  if (!session) {
    throw redirect({ to: '/auth/sign-in' });
  }

  // Operators may view the portal surface directly (e.g., to QA a render).
  // They do not have a subscriber row; short-circuit with an operator context.
  if (getRoleFromSession(session) === 'operator') {
    return {
      isOperator: true,
      subscriberId: null,
      tier: null,
      subscriberStatus: null,
    };
  }

  const subscriber = await fetchOwnSubscriber();
  if (!subscriber) {
    // Authenticated but no provisioned subscriber row. Sign them out and send
    // them to the marketing site; the discovery / subscribe flow lives there
    // after the 2026-05-28 architecture pivot.
    throw redirect({ to: '/auth/sign-out' });
  }

  // Explicit subscriber-status assertion (STEP-9 deep-dive / BATCH-6 note,
  // landed alongside the pause/cancel/email-frequency endpoints that can now
  // mutate this status). The portal is served only for the three post-
  // provisioning states; `pending` (provisioning not finished, or a status
  // that reverted active->pending after onboarding) must not reach a rendered
  // portal on the strength of the onboarding-complete check alone. paused /
  // canceled are intentionally NOT bounced here — DES-07 preserves the
  // context doc / playbook / backlog and the subscriber must be able to read
  // them; the layout banner surfaces that state (see below).
  const SERVED_STATUSES = ['active', 'paused', 'canceled'] as const;
  if (
    !(SERVED_STATUSES as readonly string[]).includes(subscriber.status)
  ) {
    // A `pending` subscriber is the OS-owner shape (Route A, scoping pass §1):
    // their home is the SEPARATE /os surface, not /portal. Without this branch
    // an OS owner who reaches the app root is routed to /portal by
    // getPostSignInPath, bounced here, and ejected to the marketing site, so
    // /os is unreachable except via a deep link. Ownership is proven by RLS via
    // fetchOwnOsEngagement() (os_engagements_owner_select, migration 0026): a
    // non-null result means the caller owns a live engagement, so route them to
    // /os instead of signing them out. Any other non-served subscriber (e.g.
    // provisioning that stalled before reaching `active`, and not an OS owner)
    // still signs out. This query runs ONLY on the non-served branch, so the
    // active-subscriber happy path takes no extra round trip.
    const osEngagement = await fetchOwnOsEngagement();
    if (osEngagement) {
      throw redirect({ to: '/os' });
    }
    throw redirect({ to: '/auth/sign-out' });
  }

  // Onboarding-complete check. READ ONLY of onboarding_state (Phase F owns
  // writes). If onboarding is not finished, route into the onboarding flow.
  const onboarding = await fetchOwnOnboardingState();
  const onboardingComplete =
    onboarding?.onboarding_status === ONBOARDING_COMPLETE_STATUS;
  if (!onboardingComplete) {
    // Phase F owns /onboarding/$token (token-gated, not auth-gated). We do
    // not have the token here, so route to the bare /onboarding resolver
    // (routes/onboarding/index.tsx), which reads the token from the current
    // user's onboarding_state row server-side and forwards to
    // /onboarding/<token>. This replaces the Phase 1 stopgap that signed the
    // user out and forced a marketing round trip + magic-link re-entry.
    throw redirect({ to: '/onboarding' });
  }

  return {
    isOperator: false,
    subscriberId: subscriber.id,
    tier: subscriber.tier,
    subscriberStatus: subscriber.status,
  };
}
